import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {jwtDecode} from 'jwt-decode'; // jwtDecode se usa sin llaves
import BASE_URL from '../config/config';
import { FiLock, FiArrowLeft, FiEye, FiEyeOff } from 'react-icons/fi';
import SelectorIdioma from '../components/ui/SelectorIdioma';

const ResetPassword = () => {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isExpired, setIsExpired] = useState(false); // Estado para verificar caducidad
  const [decoded, setDecoded] = useState(null); // Guardar el token decodificado
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [mostrarConfirmPassword, setMostrarConfirmPassword] = useState(false);

  // Capturar el token desde la URL
  const query = new URLSearchParams(useLocation().search);
  const token = query.get('token'); // Extraer el token
  const navigate = useNavigate();

  // Decodificar el token para extraer el nombre o correo del usuario
  useEffect(() => {
    try {
      if (token) {
        const decodedToken = jwtDecode(token); // Decodifica el token
        setDecoded(decodedToken); // Guarda el token decodificado
        const currentTime = Math.floor(Date.now() / 1000); // Tiempo actual en segundos (UNIX Epoch)
        if (decodedToken.exp < currentTime) {
          setIsExpired(true); // Si ha caducado, actualiza el estado
          setError(t('restablecer.errorCaducado'));
        }
      }
    } catch (err) {
      console.error('Error al decodificar el token:', err);
      setError(t('restablecer.errorInvalido'));
    }
  }, [token, t]);

  const handleSubmit = async () => {
    // Validación de contraseñas
    if (!newPassword || newPassword.length < 5) {
      setError(t('restablecer.errorCorta'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('restablecer.errorNoCoinciden'));
      return;
    }

    if (isExpired) {
      setError(t('restablecer.errorCaducadoSolicita'));
      return;
    }

    if (!decoded) {
      setError(t('restablecer.errorToken'));
      return;
    }

    try {
      // Enviar la nueva contraseña y el nombre del usuario extraído al backend
      const response = await axios.post(`${BASE_URL}/restaurar-contrasena`, {
        token, // Enviar el token completo por si el backend necesita verificarlo
        newPassword,
        usuario: decoded.username, // Extraer el correo (o nombre) desde el token decodificado
      });

      if (response.status === 200) {
        setMessage(t('restablecer.exito'));
        setError('');
        setTimeout(() => {
          navigate('/iniciar-sesion', {
            state: { successMessage: t('restablecer.exitoLogin') }
          });
        }, 1500);
      }
    } catch (error) {
      console.error('Error al restablecer la contraseña:', error);
      setError(error.response?.data?.message || t('restablecer.errorGenerico'));
    }
  };

  return (
    <main className="inicia-sesion">
      <div className="bg-intro-blob bg-intro-blob-1"></div>
      <div className="inicio-idioma"><SelectorIdioma /></div>
      <Link to="/" className="volver-inicio"><FiArrowLeft /> {t('comun.volverInicio')}</Link>
      <div className="formulario-inicio">
        <div className="logo-form-inicio">
          <img src="/images/logoadhoc.svg" alt={t('comun.marcaLogo')} />
          <p className="p-soporte">v3.2</p>
        </div>
        <h1>{t('restablecer.titulo')}</h1>
        {isExpired ? (
          <p className="error">{error}</p>
        ) : (
          <>
            <p>{t('restablecer.texto')}</p>
            <div className="campos-inicio">
              <div className="campo-inicio">
                <label htmlFor="new-password">{t('restablecer.nuevaContrasena')}</label>
                <div className="campo-inicio-input">
                  <FiLock className="campo-inicio-icon" />
                  <input
                    type={mostrarPassword ? 'text' : 'password'}
                    id="new-password"
                    placeholder={t('restablecer.placeholderNueva')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button type="button" className="campo-inicio-toggle" onClick={() => setMostrarPassword(prev => !prev)} aria-label={mostrarPassword ? t('comun.ocultarContrasena') : t('comun.mostrarContrasena')}>
                    {mostrarPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <div className="campo-inicio">
                <label htmlFor="confirm-password">{t('restablecer.confirmar')}</label>
                <div className="campo-inicio-input">
                  <FiLock className="campo-inicio-icon" />
                  <input
                    type={mostrarConfirmPassword ? 'text' : 'password'}
                    id="confirm-password"
                    placeholder={t('restablecer.placeholderConfirmar')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button type="button" className="campo-inicio-toggle" onClick={() => setMostrarConfirmPassword(prev => !prev)} aria-label={mostrarConfirmPassword ? t('comun.ocultarContrasena') : t('comun.mostrarContrasena')}>
                    {mostrarConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              {message && <p className="success">{message}</p>}
              {error && <p className="error">{error}</p>}
              <button type="button" onClick={handleSubmit}>
                {t('restablecer.boton')}
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default ResetPassword;
